import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tokenStore, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);
const USER_CACHE_KEY = "crm_user_cache_v1";

function loadCachedUser() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u && u.id ? u : null;
  } catch { return null; }
}

export function AuthProvider({ children }) {
  // Instant boot: hydrate from localStorage so the shell renders immediately,
  // then revalidate with /auth/me in the background. Prevents a full-page
  // spinner flash on every navigation/refresh.
  const cached = loadCachedUser();
  const hasToken = !!(typeof localStorage !== "undefined" && localStorage.getItem("crm_token"));
  const [user, setUser] = useState(cached || (hasToken ? null : false));
  const [error, setError] = useState("");

  const persistUser = (u) => {
    try {
      if (u && u.id) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
      else localStorage.removeItem(USER_CACHE_KEY);
    } catch {}
  };

  const fetchMe = useCallback(async () => {
    if (!hasToken && !cached) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data); persistUser(data);
    } catch {
      persistUser(null); setUser(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Heartbeat every 30s while logged in
  useEffect(() => {
    if (!user || !user.id) return;
    const send = () => api.post("/auth/heartbeat").catch(() => {});
    send();
    const iv = setInterval(send, 30000);
    return () => clearInterval(iv);
  }, [user]);

  const login = async (email, password) => {
    setError("");
    try {
      // Persistent device id for session tracking
      let deviceId = localStorage.getItem("crm_device_id");
      if (!deviceId) {
        deviceId = (crypto.randomUUID && crypto.randomUUID()) || `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem("crm_device_id", deviceId);
      }
      const deviceName = navigator.userAgent ? navigator.userAgent.split(")")[0].slice(0, 80) : "web";
      const { data } = await api.post("/auth/login", { email, password, device_id: deviceId, device_name: deviceName });
      if (data.access_token) tokenStore.set(data.access_token);
      if (data.session_id) localStorage.setItem("crm_session_id", data.session_id);
      setUser(data.user); persistUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    tokenStore.clear();
    localStorage.removeItem("crm_session_id");
    persistUser(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, error, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
